import { useEffect, useRef } from "react";
import { useAuth } from "./useAuth";
import { useRole } from "./useRole";
import { supabase } from "@/integrations/supabase/client";

interface ClarityWindow extends Window {
  clarity?: (...args: any[]) => void;
}

declare const window: ClarityWindow;

export function useClarity() {
  const { user } = useAuth();
  const { isAdmin, isSuperAdmin } = useRole();
  const loadedRef = useRef(false);

  useEffect(() => {
    if (!user) return;
    if (isAdmin || isSuperAdmin) return;
    if (loadedRef.current) return;
    if (typeof window === "undefined") return;

    let cancelled = false;

    async function init() {
      // Fetch project ID from system_config (admin-configurable)
      const { data } = await (supabase as any)
        .from("system_config")
        .select("value")
        .eq("key", "clarity_project_id")
        .maybeSingle();

      const projectId = data?.value || import.meta.env.VITE_CLARITY_PROJECT_ID;
      if (!projectId) return;
      if (cancelled) return;

      loadedRef.current = true;

      // Load Clarity script
      const clarityScript = document.createElement("script");
      clarityScript.type = "text/javascript";
      clarityScript.async = true;
      clarityScript.src = `https://www.clarity.ms/tag/${projectId}`;
      document.head.appendChild(clarityScript);

      // Wait for clarity to be ready then identify
      const tryIdentify = () => {
        if (typeof window.clarity !== "function") {
          setTimeout(tryIdentify, 500);
          return;
        }
        // Fetch user_number from profiles
        (supabase as any)
          .from("profiles")
          .select("user_number, email")
          .eq("id", user.id)
          .maybeSingle()
          .then(({ data }: any) => {
            if (!data) return;
            const num = data.user_number;
            const email = data.email || user.email;
            if (num != null) {
              window.clarity!("identify", String(num));
              window.clarity!("set", "user_number", String(num));
            }
            if (email) {
              window.clarity!("set", "user_email", email);
            }
          });
      };
      tryIdentify();
    }

    init();
    return () => { cancelled = true; };
  }, [user, isAdmin, isSuperAdmin]);
}
